import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { IListLayout } from 'src/app/models/interfaces/list-layout.interface';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { ListLayoutWorkflowsFormContext } from '../list-layout-workflows-form-context';

@Component({
    selector: 'app-list-layout-workflows-form',
    templateUrl: './list-layout-workflows-form.component.html',
    styleUrls: ['./list-layout-workflows-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        MatCheckboxModule,
        MatButtonModule,
        NgClass,
        MatSelectModule,
        RichToolTipDirective,
    ],
})
export class ListLayoutWorkflowsFormComponent extends ManagementFormComponent<IListLayout> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos du client à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('listLayout')
    public dataInput: Required<IListLayout> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: ListLayoutWorkflowsFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<ListLayoutWorkflowsFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: ListLayoutWorkflowsFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('listLayoutUpdated')
    public dataOutput: EventEmitter<IListLayout> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /** Type de tri */
    public sorts = [
        { value: 'asc', name: 'Ascendant' },
        { value: 'desc', name: 'Descendant' },
    ];

    defautSortValue = 'toto';

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);

            if (this._context?.mode === FormMode.Edition && this.dataValue && this.dataValue.name === 'Default') {
                this.formGroup.get('name').disable();
            }

            if (this._context.mode === FormMode.Creation) {
                // Applique le valeurs du tri par défaut
                this.formGroup.get('sortAttributeId').setValue(2); // Attribut système date de création
                this.formGroup.get('sortDirection').setValue('desc'); // tri ascendant
            }

            this.defautSortValue = `${this._context.attributes.find((at) => at.id === x.item.sortAttributeId)?.header} ${this.sorts.find((st) => st.value === x.item.sortDirection)?.name}`;
        });

        if (this.dataValue && this.dataValue.isScreenSizeAdapted) {
            this.formGroup.get('workflowStepColumnWidth').disable();
        }
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();

        if (this.dataValue && this.dataValue.name === 'Default') {
            this.formGroup.get('name').disable();
        }
    }

    public isScreenSizeAdaptedChanged(event) {
        if (event.checked) {
            this.formGroup.get('workflowStepColumnWidth').disable();
        } else {
            this.formGroup.get('workflowStepColumnWidth').enable();
        }
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IListLayout> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            organizationId: new UntypedFormControl(null),
            name: new UntypedFormControl(null, Validators.required),
            isScreenSizeAdapted: new UntypedFormControl(null),
            workflowStepColumnWidth: new UntypedFormControl(null),
            recordsPerPage: new UntypedFormControl(null, [Validators.required, Validators.min(50)]),
            sortAttributeId: new UntypedFormControl(null),
            sortDirection: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        if (this.formGroup.get('name').disabled === true) {
            this.dataValue.name = 'Default';
        }
        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
